import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react';
import { Notice as UiNotice } from '@/components/ui/Notice';
import { EmailLink } from '@/components/ui/TextLink';
import { ApiError, api } from '@/lib/api';
import {
  DOCUSIGN_ENVELOPE_IS_EMAILED,
  SIGNING_COMPLETE_EVENT,
  docuSignEnvironmentLabel,
  isEmbeddedDelivery,
  signatureConsequenceNote,
  signaturesNotBinding,
  signingReturnIsProof,
  signingUrlRefusal,
  type EmbeddedDelivery,
} from '@/lib/docusign';
import { formatDateTime } from '@/lib/format';
import type { DocuSignConnection, DocuSignEnvelope } from '@/lib/types';
import { Badge, Button, Input, Modal } from '@/components/ui';

/*
 * SigningLinkDialog — mint a single-use signing URL and hand it straight to the
 * person standing there.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * The link is a CREDENTIAL, not a page reference
 * ────────────────────────────────────────────────────────────────────────────
 *
 * `POST /docusign/envelopes/{id}/signing-url` mints a URL that is SINGLE USE and
 * dies in roughly five minutes. The backend answers it `Cache-Control: no-store`
 * and made it a POST for exactly that reason — a GET would be cached by a
 * browser, a proxy or a service worker, leaving a dead link and a live credential
 * in a cache at the same time.
 *
 * Everything in this file follows from that one property:
 *
 * - It is minted on an explicit press, never on open, never in advance.
 * - It lives in `MintPanel`'s local state and nowhere else. `Modal` returns null
 *   when closed, so `MintPanel` UNMOUNTS and the URL is destroyed with it — the
 *   dialog closing is what discards the link, not a cleanup somebody has to
 *   remember. Nothing reaches `localStorage`, `sessionStorage`, a ref that
 *   outlives the panel, the address bar, or the parent.
 * - It is never rendered as an `<a href>`. A link the page keeps is a link
 *   somebody bookmarks, screenshots or forwards, and it will be dead by then.
 * - A clock discards it at expiry rather than leaving it on screen looking
 *   usable. **A stale link handed to a customer fails in a way that looks like
 *   the system is broken**, and the customer is the one person who cannot tell
 *   the difference.
 * - There is deliberately **no email, mailto or download path.** Delivering it by
 *   mail is precisely what its lifetime forbids, and offering the affordance is
 *   how somebody finds that out at a customer's expense.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * `?event=signing_complete` IS NOT PROOF, and nothing here reads it
 * ────────────────────────────────────────────────────────────────────────────
 *
 * DocuSign appends `?event=signing_complete` (or `cancel`, `decline`,
 * `ttl_expired`) to the return URL. That is a client-side redirect and the signer
 * can retype it in the address bar. **No code in this file reads
 * `window.location.search`**, and none may be added: the only truth is
 * `POST /docusign/envelopes/{id}/refresh` and the `status` it writes back — there
 * is no webhook, so nothing else can tell us anything. `signingReturnIsProof()`
 * from `@/lib/docusign` is the deliberately useless function that answers the
 * temptation at the one place it arises, and it is called below so the answer is
 * in the code rather than only in a comment.
 *
 * ────────────────────────────────────────────────────────────────────────────
 * A signature ACCEPTS the quote
 * ────────────────────────────────────────────────────────────────────────────
 *
 * The refresh that observes `completed` is not a read. It moves the quote to
 * the accepting stage — and nothing more; no invoice, no Xero push. Nothing
 * asks for a confirmation, by design — the customer signing IS the
 * confirmation and nobody is at a keyboard to ask. This dialog is often the
 * last screen before somebody signs, so `signatureConsequenceNote()` is
 * rendered in front of the mint control rather than left to be discovered.
 */

/* -------------------------------------------------------------------------- */
/* returnUrl                                                                   */
/* -------------------------------------------------------------------------- */

/**
 * Where DocuSign sends the browser when signing finishes.
 *
 * ⚠️ **Built from our own origin and from nothing else.** The backend checks this
 * against its CORS allow-list and answers `422 path: "returnUrl"` otherwise, and
 * that check is not decoration. In their words: an unvalidated value is *an open
 * redirect with a difference — the redirect is performed by docusign.com, so the
 * signer sees a trusted domain hand them to an attacker's page mid-signature,
 * which is the moment they are most primed to enter something.*
 *
 * So there are exactly two inputs and neither can be influenced from outside this
 * bundle:
 *
 * 1. `window.location.origin` — the browser's own serialisation of the origin
 *    this bundle was served from. Not `document.referrer`, not a query
 *    parameter, not a prop, not a configured base URL, not anything a caller can
 *    pass in: **this function takes no string argument at all**, so there is no
 *    parameter for a tainted value to arrive through.
 * 2. The quote id, which arrives on the envelope from our own API and is
 *    additionally required here to be a positive safe integer before it is
 *    interpolated. That check is what makes the path provably a path — a value
 *    like `//evil.example` is origin-relative to `new URL()` and would otherwise
 *    escape. Anything failing it falls back to the bare list route.
 *
 * `pathname` is ASSIGNED rather than the path being concatenated into a string,
 * for the same reason: setting `pathname` on a `URL` cannot change that URL's
 * origin, whatever it is handed.
 *
 * A `422` here is the guard working, and it renders as one — never as a retry.
 */
function returnUrlFor(quoteId: unknown): string {
  const url = new URL(window.location.origin);
  url.pathname =
    typeof quoteId === 'number' && Number.isSafeInteger(quoteId) && quoteId > 0
      ? `/quotes/${quoteId}`
      : '/quotes';
  return url.toString();
}

/* -------------------------------------------------------------------------- */
/* Small shared pieces                                                         */
/* -------------------------------------------------------------------------- */

function Notice({
  tone,
  title,
  children,
}: {
  tone: 'warning' | 'danger' | 'info';
  title: string;
  children?: ReactNode;
}) {
  // The shared primitive draws the box; the role is still derived from the tone
  // here, exactly as it was, so a refusal is announced and a fact is not.
  return (
    <UiNotice
      tone={tone}
      role={tone === 'danger' ? 'alert' : 'status'}
      title={title}
      className="[overflow-wrap:anywhere] [&_p]:m-0 [&_p+p]:mt-1"
    >
      {children}
    </UiNotice>
  );
}

/**
 * The sandbox warning, from the ONE copy of the rule in `@/lib/docusign`.
 *
 * `signaturesNotBinding()` is deliberately a negative check — read its doc
 * comment before touching this. Never re-derive it as `environment === 'demo'`:
 * that would silently stop warning the day a third environment appears, and it
 * cannot express the state that matters most here, which is having no connection
 * to read at all.
 *
 * Note this dialog takes any write role while `GET /docusign/connection` is
 * **admin only**, so `connection` is legitimately `null` for most of the people
 * who will see this screen. `null` warns, which is the right way round: the
 * moment we know least is not the moment to imply a signature is enforceable.
 */
function NotBindingWarning({ connection }: { connection: DocuSignConnection | null }) {
  return (
    <Notice
      tone="warning"
      title={
        connection
          ? connection.environment === 'production'
            ? 'Environment mismatch — this signature may not be legally binding'
            : 'Demo — this signature will not be legally binding'
          : 'Unconfirmed — treat this signature as not legally binding'
      }
    >
      <p>
        {connection
          ? 'Sandbox envelope — a completed signature is not enforceable.'
          : 'The connection could not be read, so treat any signature here as unenforceable.'}
      </p>
      <p>
        Environment: {docuSignEnvironmentLabel(connection?.environment)} &middot; Host:{' '}
        {connection?.account?.baseUri ?? 'not reported'}
      </p>
    </Notice>
  );
}

/**
 * What a completed signature does, stated before anybody signs.
 *
 * The sentence comes from `signatureConsequenceNote()` rather than being written
 * here, because it has to state **both** halves at once when both are true: the
 * signature carries no legal force on a sandbox connection *and* the stage
 * move it causes is entirely real. Picking one of two simultaneously true
 * sentences is this repo's recurring `formError ?? count` silent drop, and
 * that helper exists to make it one decision in one place.
 *
 * Rendered wherever a signature is still possible, the emailed branch included —
 * an emailed signer can sign just as well as one standing here, and the person
 * reading this screen is the one who can still void the envelope.
 */
function AcceptanceConsequence({ connection }: { connection: DocuSignConnection | null }) {
  return (
    <Notice tone="info" title="Signing accepts the quote. Nothing asks again.">
      <p>{signatureConsequenceNote(connection)}</p>
      <p>Billing is a separate step on the quote.</p>
    </Notice>
  );
}

/** One labelled fact about the envelope. Pairs, matching the DocuSign card. */
function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-xs font-medium text-fg-subtle">{label}</dt>
      <dd className="m-0 mt-0.5 min-w-0 text-sm text-fg-secondary [overflow-wrap:anywhere]">
        {children}
      </dd>
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The refresh control — the ONLY thing that may report a signature            */
/* -------------------------------------------------------------------------- */

/**
 * `POST /docusign/envelopes/{id}/refresh`, and the only source of truth in this
 * file about whether anything was signed.
 *
 * It is a POST because it mutates, and the mutation is not only the stored
 * status: a refresh that observes `completed` also moves the quote to
 * `Accepted`. That is best-effort server-side and deliberately re-run on every
 * call, so pressing this again on a signed quote that still reads as unsigned
 * is a legitimate repair rather than a wasted request — which is why the
 * control stays available after a terminal answer instead of disabling itself.
 *
 * `onRefreshed` exists because that stage move happens with nobody in EMERGE
 * touching anything: any quote list, detail page or board open elsewhere is now
 * stale, and the parent is the only thing that can re-read it.
 *
 * ⚠️ **IT NEEDS A WRITE ROLE as of 2026-08-29** — release
 * `20260829123506-c323947` put `WRITE_ROLES` on that route. **There is no gate
 * on this control and there should not be one**, because the gate is already
 * upstream and unavoidable: this whole dialog is reached only through
 * `EnvelopePanel`'s "Get signing link…", which is itself behind `canManage`. A
 * second copy of the same check here would be a second thing to keep in step
 * with the ability model, and it would drift.
 *
 * ⚠️ **The `403` is still reachable, and the `.catch` below is what handles
 * it.** A role can be taken away under a live access token — they last about
 * five days — so a session can hold this dialog open with an authority it no
 * longer has. It renders the server's own sentence as a block; a `403` carries
 * no `details`, so it must never be attached to a field.
 */
function RefreshControl({
  envelope,
  onRefreshed,
}: {
  envelope: DocuSignEnvelope;
  onRefreshed?: (envelope: DocuSignEnvelope) => void;
}) {
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState<DocuSignEnvelope | null>(null);
  const [error, setError] = useState<string | null>(null);
  /*
   * ⚠️ `api.refreshDocusignEnvelope` takes no `AbortSignal`, so the house rule
   * that every fetch passes one cannot be met here — the request runs to
   * completion whatever this component does. A liveness ref stands in for the
   * half that is ours to control: no state is written after unmount.
   *
   * That is the right way round for THIS call. A refresh is a mutation the
   * server should finish — it may be the call that observes the signature and
   * moves the quote to Accepted — so cancelling it mid-flight would abandon
   * work that is wanted, not save any.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  function check() {
    setBusy(true);
    setError(null);
    api
      .refreshDocusignEnvelope(envelope.id)
      .then((response) => {
        if (!alive.current) return;
        setResult(response.data);
        onRefreshed?.(response.data);
      })
      .catch((caught: unknown) => {
        if (!alive.current) return;
        /* 401 belongs to `api.ts`, which is already refreshing or signing out. */
        if (caught instanceof ApiError && caught.status === 401) return;
        setError(
          caught instanceof ApiError
            ? (caught.formMessage ?? caught.message)
            : caught instanceof Error
              ? caught.message
              : 'Could not ask DocuSign for the current status.',
        );
      })
      .finally(() => {
        if (!alive.current) return;
        setBusy(false);
      });
  }

  const shown = result ?? envelope;

  return (
    <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
      <p className="m-0 text-sm font-medium text-fg">When the signer has finished</p>
      <p className="m-0 text-xs text-fg-secondary [overflow-wrap:anywhere]">
        DocuSign puts <code className="font-mono">?event={SIGNING_COMPLETE_EVENT}</code> on the
        address it returns to.{' '}
        {signingReturnIsProof()
          ? /* Unreachable: the helper is typed as the literal `false`. It is called rather
               than quoted so this claim is checked by the compiler, not by a reader. */
            'That would be proof.'
          : 'It is not proof — ask DocuSign.'}
      </p>
      {/*
       * ⛔ **`STATUS_IS_LAST_KNOWN_NOTE` prefixed this line and was removed —
       * owner's helper-text ruling, 2026-09-05.**
       *
       * ⚠️ **The TIMESTAMP is not helper text and stays.** It is a value, and it
       * is the one that keeps the surviving wording honest: the label is *"Last
       * checked"*, never *"status as of"*, so the figure still says somebody
       * asked at a moment rather than that anything is being watched. **Do not
       * relabel it**, and do not let a future status column here drop the check
       * time — a status with no check time beside it reads as current, and there
       * is still no webhook.
       */}
      <p className="m-0 text-xs text-fg-subtle [overflow-wrap:anywhere]">
        Last checked: {shown.statusCheckedAt ? formatDateTime(shown.statusCheckedAt) : 'never'}.
      </p>
      <div>
        <Button variant="secondary" size="sm" onClick={check} disabled={busy}>
          {busy ? 'Asking DocuSign…' : 'Check with DocuSign'}
        </Button>
      </div>
      {error && (
        <Notice tone="danger" title="Could not check the status.">
          <p>{error}</p>
        </Notice>
      )}
      {result && (
        <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1 text-xs text-fg-secondary">
          <Badge tone={result.signed ? 'success' : result.terminal ? 'neutral' : 'info'} dot>
            {result.status}
          </Badge>
          <span className="min-w-0 [overflow-wrap:anywhere]">
            {result.signed
              ? 'DocuSign reports this completed, so the quote has been accepted. Re-read the quote.'
              : result.terminal
                ? 'DocuSign reports nothing further will happen to this envelope.'
                : 'DocuSign reports this is still out for signature.'}
          </span>
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* MintPanel — the ONLY place a signing URL is requested                       */
/* -------------------------------------------------------------------------- */

interface MintedLink {
  url: string;
  /** Wall-clock deadline, so the countdown cannot drift across re-renders. */
  expiresAt: number;
}

interface MintFailure {
  message: string;
  /**
   * False for the two `409`s that can never succeed on a retry, and for the
   * `returnUrl` refusal. A retry button on those is a button whose only possible
   * outcome is the same error.
   */
  retryable: boolean;
}

/** DocuSign's documented lifetime, used when the server sends nothing usable. */
const FALLBACK_EXPIRY_SECONDS = 300;
/** A ceiling on the server's own figure. A clock that is wrong must be SHORT. */
const MAX_EXPIRY_SECONDS = 900;

/**
 * Mints the link, runs the clock, and hands it over.
 *
 * ⚠️ **The prop type is the embedded/email rule.** It takes
 * `EmbeddedDelivery<DocuSignEnvelope>` — an envelope proven at the type level to
 * be `delivery: 'embedded'` — and `api.docusignSigningUrl` takes a
 * `SignableEnvelopeRef` for the same reason. So an emailed envelope cannot reach
 * a mint by any route: `SigningLinkDialog` has to narrow with
 * `isEmbeddedDelivery()` first, and a call site that forgets **fails `tsc`**
 * rather than reaching a `409` at runtime. Same pattern as `DealFormModal`
 * keeping `projectId` out of `FormValues`: a payload that cannot express the
 * mistake beats a guard somebody can forget.
 *
 * The `409` branches below are kept anyway, and they are the guarantee rather
 * than the optimisation. `delivery` was briefly wrong on the wire (the column was
 * missing from the repository's `SELECT`, so the serialiser answered its
 * default), an envelope can go terminal while this dialog is open, and an access
 * token here lasts over five days.
 */
function MintPanel({ envelope }: { envelope: EmbeddedDelivery<DocuSignEnvelope> }) {
  const [link, setLink] = useState<MintedLink | null>(null);
  const [minting, setMinting] = useState(false);
  const [failure, setFailure] = useState<MintFailure | null>(null);
  const [expired, setExpired] = useState(false);
  const [copy, setCopy] = useState<'idle' | 'copied' | 'refused'>('idle');
  /** Revealed only when copying fails, so there is always a way to get it across. */
  const [revealed, setRevealed] = useState(false);
  const [secondsLeft, setSecondsLeft] = useState(0);
  /*
   * ⚠️ `api.docusignSigningUrl` takes no `AbortSignal`, so a mint already in
   * flight when this panel unmounts cannot be cancelled: the server still mints a
   * real credential and nobody ever holds it. It dies on its own in five minutes,
   * which is the whole reason that is tolerable — but it is a genuine loose end
   * rather than a tidy one, and the fix belongs in `api.ts`, not in a workaround
   * here.
   *
   * What IS enforced here: the URL never lands in state after unmount, so it
   * cannot outlive the dialog even by a tick.
   */
  const alive = useRef(true);
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);

  /*
   * The clock is not decoration. A link that has quietly died while sitting on
   * screen is worse than no link: it gets handed over, it fails, and it fails
   * looking like our fault. At zero the URL is DROPPED from state rather than
   * merely labelled — so there is nothing left to copy, open or screenshot.
   */
  useEffect(() => {
    if (!link) return;
    const { expiresAt } = link;
    function tick() {
      const remaining = Math.ceil((expiresAt - Date.now()) / 1000);
      if (remaining <= 0) {
        setLink(null);
        setExpired(true);
        setRevealed(false);
        setCopy('idle');
        setSecondsLeft(0);
        return;
      }
      setSecondsLeft(remaining);
    }
    tick();
    const timer = window.setInterval(tick, 1000);
    return () => window.clearInterval(timer);
  }, [link]);

  const mint = useCallback(() => {
    setMinting(true);
    setFailure(null);
    setExpired(false);
    setCopy('idle');
    setRevealed(false);

    api
      /*
       * `envelope` is passed whole, not `envelope.id`: the method takes a
       * `SignableEnvelopeRef`, which demands `delivery: 'embedded'`. That is what
       * makes an emailed mint a COMPILE error rather than a runtime 409.
       */
      .createDocusignSigningUrl(envelope, { returnUrl: returnUrlFor(envelope.quote.id) })
      .then(({ data }) => {
        if (!alive.current) return;
        /*
         * Trust the server's figure, but never trust it to be present or sane —
         * a missing or absurd value must SHORTEN the clock, never remove it. An
         * expiry we render longer than the real one is the exact failure this
         * whole panel exists to avoid.
         */
        const seconds =
          typeof data.expiresInSeconds === 'number' && data.expiresInSeconds > 0
            ? Math.min(data.expiresInSeconds, MAX_EXPIRY_SECONDS)
            : FALLBACK_EXPIRY_SECONDS;
        setLink({ url: data.url, expiresAt: Date.now() + seconds * 1000 });
      })
      .catch((caught: unknown) => {
        if (!alive.current) return;
        if (caught instanceof ApiError && caught.status === 401) return;
        if (caught instanceof ApiError) {
          /*
           * Route on `error.code`, never on `error.message`. The messages are
           * written for a person and get reworded; the codes are the contract.
           * These two `409`s mean opposite things and want opposite UI, and a
           * message match cannot tell them apart.
           */
          if (caught.code === DOCUSIGN_ENVELOPE_IS_EMAILED) {
            setFailure({
              message:
                'DocuSign emailed this envelope, so there is no link to mint. Void it and send a new one as a link.',
              retryable: false,
            });
            return;
          }
          if (caught.status === 409) {
            setFailure({
              message: `${caught.message} There is nothing left to sign on this envelope.`,
              retryable: false,
            });
            return;
          }
          /*
           * A refusal naming `returnUrl` is the CORS allow-list doing its job.
           * It is not retryable and it is not the user's mistake — the address
           * is built from this origin, so the origin this app is served from is
           * not one the API allows, which is a deployment fact rather than
           * anything a person here can fix by pressing the button again.
           */
          if (caught.fieldErrors?.returnUrl) {
            setFailure({
              message: `${caught.fieldErrors.returnUrl} This site's address is not one the API allows signers back to. Nothing here can change that.`,
              retryable: false,
            });
            return;
          }
          setFailure({ message: caught.formMessage ?? caught.message, retryable: true });
          return;
        }
        setFailure({
          message:
            caught instanceof Error ? caught.message : 'Could not mint a signing link just now.',
          retryable: true,
        });
      })
      .finally(() => {
        if (!alive.current) return;
        setMinting(false);
      });
  }, [envelope]);

  function openSigningPage() {
    if (!link) return;
    window.open(link.url, '_blank', 'noopener,noreferrer');
  }

  /*
   * `navigator.clipboard` needs a secure context and can reject anyway —
   * permissions policy, an unfocused document, an embedded frame. **A copy
   * button that silently does nothing is worse than no copy button**: somebody
   * pastes whatever was on the clipboard before and hands over the wrong thing.
   * So a refusal is stated AND the URL is revealed as selectable text, which is
   * a way through rather than an apology.
   */
  function copyLink() {
    if (!link) return;
    let write: Promise<void> | undefined;
    try {
      write = navigator.clipboard?.writeText(link.url);
    } catch {
      /* Some browsers throw synchronously rather than rejecting. Same outcome. */
      write = undefined;
    }
    if (!write) {
      setCopy('refused');
      setRevealed(true);
      return;
    }
    write.then(
      () => setCopy('copied'),
      () => {
        setCopy('refused');
        setRevealed(true);
      },
    );
  }

  const canRetry = failure ? failure.retryable : true;

  return (
    <div className="flex flex-col gap-3">
      <Notice tone="warning" title="Mint this only with the signer in front of you.">
        <p>Works once, dies after about five minutes. Do not store, forward or email it.</p>
        <p>Closing this dialog discards it for good.</p>
      </Notice>

      {!link && (
        <div>
          <Button
            variant="primary"
            onClick={mint}
            disabled={minting || !canRetry}
            className="max-sm:w-full"
          >
            {minting
              ? 'Minting…'
              : expired || failure
                ? 'Mint another link'
                : 'Mint the signing link'}
          </Button>
        </div>
      )}

      {expired && !link && !failure && (
        <Notice tone="warning" title="That link expired and has been discarded.">
          <p>Nothing was signed. Mint another when the signer is ready.</p>
        </Notice>
      )}

      {failure && (
        <Notice tone="danger" title="No signing link was created.">
          <p>{failure.message}</p>
        </Notice>
      )}

      {link && (
        <div className="flex flex-col gap-2 rounded-md border border-border bg-surface-sunken px-3 py-2.5">
          <div className="flex min-w-0 flex-wrap items-center gap-x-2 gap-y-1">
            <Badge tone={secondsLeft <= 60 ? 'warning' : 'info'} dot>
              Expires in {Math.floor(secondsLeft / 60)}:{String(secondsLeft % 60).padStart(2, '0')}
            </Badge>
            <Badge tone="neutral" variant="outline">Single use</Badge>
          </div>
          <p className="m-0 text-xs text-fg-secondary">
            Open it on the signer&rsquo;s screen now. It will not be shown again.
          </p>
          <div className="flex flex-wrap gap-2">
            <Button variant="primary" size="sm" onClick={openSigningPage} className="max-sm:w-full">
              Open the signing page
            </Button>
            <Button variant="secondary" size="sm" onClick={copyLink} className="max-sm:w-full">
              Copy the link
            </Button>
          </div>
          {copy === 'copied' && (
            <p role="status" className="m-0 text-xs text-success">
              Copied. Paste it where the signer can click it now.
            </p>
          )}
          {copy === 'refused' && (
            <Notice tone="warning" title="Your browser refused to copy it.">
              <p>
                Select the address below and copy it by hand. Do not email it.
              </p>
            </Notice>
          )}
          {revealed && (
            <Input
              readOnly
              value={link.url}
              aria-label="Signing link"
              className="font-mono text-xs"
              onFocus={(event) => event.currentTarget.select()}
            />
          )}
        </div>
      )}
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* The dialog                                                                  */
/* -------------------------------------------------------------------------- */

export interface SigningLinkDialogProps {
  open: boolean;
  onClose: () => void;
  /** The envelope as last read. `delivery` and `terminal` decide the whole shape. */
  envelope: DocuSignEnvelope;
  /**
   * `GET /docusign/connection` if the caller has it. It is **admin only**, so
   * `null` is the ordinary case for the write roles that reach this dialog — and
   * `null` is the state the sandbox warning treats most cautiously.
   */
  connection?: DocuSignConnection | null;
  /** `ability.canUpdate('quotes')`. Minting is `WRITE_ROLES` on the API. */
  canMint: boolean;
  /** The true reason minting is unavailable, from `denialReason()`. */
  mintDenial?: string | null;
  /**
   * Called with the envelope a refresh returned. A refresh that observes a
   * signature moves the quote's stage server-side, so the parent must re-read the
   * quote — nothing in EMERGE pressed anything and no other screen knows.
   */
  onEnvelopeRefreshed?: (envelope: DocuSignEnvelope) => void;
}

export function SigningLinkDialog({
  open,
  onClose,
  envelope,
  connection = null,
  canMint,
  mintDenial = null,
  onEnvelopeRefreshed,
}: SigningLinkDialogProps) {
  /*
   * `signingUrlRefusal()` is the runtime explanation — 'emailed', 'terminal' or
   * null — and `isEmbeddedDelivery()` below is the type-level guarantee. Both are
   * needed and they are not duplicates: `terminal` changes under a live record
   * and cannot be encoded in a type, while `delivery` can and therefore should
   * be, so that no future edit to this file can route an emailed envelope into a
   * mint without failing the build.
   */
  const refusal = signingUrlRefusal(envelope);

  return (
    <Modal
      open={open}
      onClose={onClose}
      title="Signing link"
      /*
       * `md`, not `xl`. This is a column of sentences and two buttons; `xl` is for
       * a wide table. Below `sm` every size is the same full-bleed sheet, so at
       * 320px this is a single column either way — the only multi-column thing
       * here is the facts list, and it is `sm:grid-cols-2`, one column below that.
       */
      size="md"
      footer={
        <Button variant="secondary" onClick={onClose}>
          Close
        </Button>
      }
    >
      <div className="flex flex-col gap-3">
        {/*
         * Above everything, in every branch. The credentials on the server are the
         * DocuSign sandbox deliberately, so the failure mode looks exactly like
         * success — and the person about to take a signature is the one who has to
         * know it is worth nothing.
         */}
        {signaturesNotBinding(connection) && <NotBindingWarning connection={connection} />}

        <dl className="grid gap-3 sm:grid-cols-2">
          <Fact label="Quote">{envelope.quote.quoteNumber ?? `#${envelope.quote.id}`}</Fact>
          <Fact label="Signer">
            {envelope.signer.name}
            {envelope.signer.email ? (
              <>
                {' · '}
                <EmailLink address={envelope.signer.email} />
              </>
            ) : null}
          </Fact>
          <Fact label="Status">
            <Badge
              tone={envelope.signed ? 'success' : envelope.terminal ? 'neutral' : 'info'}
              dot
            >
              {envelope.status}
            </Badge>
          </Fact>
          <Fact label="Delivery">
            {refusal === 'emailed' ? 'DocuSign emails the signer' : 'You hand the signer a link'}
          </Fact>
        </dl>

        {/*
         * ⚠️ The emailed state is UNREACHABLE, not handled. `isEmbeddedDelivery()`
         * narrows the envelope, and `MintPanel` accepts only the narrowed type —
         * so on an emailed envelope there is no mint control to disable, no early
         * return to forget, and no call `tsc` would let through. Calling
         * `signing-url` on one is a `409` whose only purpose would be to discover
         * something `delivery` already said.
         */}
        {refusal === 'emailed' ? (
          <>
            <Notice tone="info" title="DocuSign has already emailed the signer.">
              <p>
                The signer signs from the message sent to{' '}
                <EmailLink address={envelope.signer.email} />, so there is no
                link. Void this and send a new one as a link if you need one.
              </p>
            </Notice>
            <AcceptanceConsequence connection={connection} />
            <RefreshControl envelope={envelope} onRefreshed={onEnvelopeRefreshed} />
          </>
        ) : refusal === 'terminal' ? (
          <>
            <Notice
              tone="info"
              title={
                envelope.signed
                  ? 'This envelope has been signed.'
                  : `Nothing further will happen to this envelope (${envelope.status}).`
              }
            >
              <p>
                {envelope.signed
                  ? 'The quote is accepted and the signed document is with DocuSign. No link can be minted.'
                  : 'Nothing left to sign, so no link can be minted. Send a new envelope if needed.'}
              </p>
              {envelope.voidedReason && <p>Reason given: {envelope.voidedReason}</p>}
            </Notice>
            {/*
             * Still offered on a terminal envelope, deliberately: a refresh
             * re-runs the stage move and the Xero attachment, both idempotent, so
             * this is the repair for a signed quote that still reads as unsigned.
             */}
            <RefreshControl envelope={envelope} onRefreshed={onEnvelopeRefreshed} />
          </>
        ) : !canMint ? (
          /*
           * The reason is text, not only a `title`: a disabled control has
           * `pointer-events: none`, so its tooltip never reaches the person it was
           * written for. There is no disabled mint button here at all — an
           * affordance whose only outcome is a 403 is not an affordance.
           */
          <>
            <Notice tone="info" title="You cannot mint a signing link.">
              <p>
                {mintDenial ?? 'Needs an account with permission to change quotes.'}
              </p>
            </Notice>
            <RefreshControl envelope={envelope} onRefreshed={onEnvelopeRefreshed} />
          </>
        ) : isEmbeddedDelivery(envelope) ? (
          <>
            {/* Stated BEFORE the mint control — this is the last screen before a signature. */}
            <AcceptanceConsequence connection={connection} />
            <MintPanel envelope={envelope} />
            <RefreshControl envelope={envelope} onRefreshed={onEnvelopeRefreshed} />
          </>
        ) : null /* Unreachable: `refusal` is null, so delivery is embedded. `tsc` needs the narrowing, not a cast. */}
      </div>
    </Modal>
  );
}
